/* eslint-disable no-unused-vars */
import userCartService from "@/services/userCartService";
import signatureMakercartService from "@/services/signatureMakercartService";
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useLocaleStore } from "@/store/locale";
import { getCustomCartConfiguration } from "@/utils/customCartConfiguration";

const makercarts = ref([]);
const singleActivities = ref([]);
const signatureMakercarts = ref([]);
const makercrates = ref([]);
const total = ref({
  grandTotal: 0,
  originalPrice: 0,
  percartTotal: 0,
  posterPrice: 0,
  singleSubtotal: 0,
  signaturePrice: 0,
  customCartPrice: 0,
  makercratePrice: 0,
});
const quoteRequested = ref(false);
const cartId = ref(null);
const quoteGenerated = ref(false);
let cartIdCounter = 1;
let makercartsInitialized = false;
let makercartsInitPromise = null;
const cartLoadingCount = ref(0);

const isCartLoading = computed(() => cartLoadingCount.value > 0);

/**
 * Composable for cart functionality using Vuex store and localStorage
 * This provides a clean API for cart operations and reactive state
 */
export function useCart() {
  const store = useStore();

  const localeStore = useLocaleStore();
  const locale = computed(() => localeStore.currentLocale);

  const cartItems = computed(() => store.getters.getCartItems);
  const isCartEmpty = computed(() => store.getters.isCartEmpty);
  const isCartSubmitting = computed(() => store.getters.isCartSubmitting);
  const canSubmitCart = computed(() => store.getters.canSubmitCart);
  const cartValidationMessage = computed(
    () => store.getters.getCartValidationMessage
  );
  const cartLastUpdated = computed(() => store.getters.getCartLastUpdated);
  const shouldAutoProcessCart = computed(
    () => store.getters.getShouldAutoProcessCart
  );

  const getTotalActivities = () => {
    const cartActivitiesTotal = (makercarts.value || []).reduce((sum, cart) => {
      const activities = Array.isArray(cart.activities) ? cart.activities : [];
      return sum + activities.length;
    }, 0);

    const signatureCartActivitiesTotal = (
      signatureMakercarts.value || []
    ).reduce((sum, cart) => {
      const activities = Array.isArray(cart.activities) ? cart.activities : [];
      return sum + activities.length;
    }, 0);

    const makercrateActivitiesTotal = (makercrates.value || []).reduce(
      (sum, crate) => {
        const activities = Array.isArray(crate.activities)
          ? crate.activities
          : [];
        // Prefer activity rows when present; otherwise count kit quantity
        // so makercrate-only carts still update the View Cart badge.
        if (activities.length > 0) {
          return sum + activities.length;
        }
        return sum + (Number(crate.quantity) || 1);
      },
      0
    );

    const singleActivitiesTotal = singleActivities.value.length;
    return (
      cartActivitiesTotal +
      singleActivitiesTotal +
      signatureCartActivitiesTotal +
      makercrateActivitiesTotal
    );
  };

  const totalActivities = computed(() => getTotalActivities());

  const hasFullActivityCart = computed(() => {
    return makercarts.value.some((cart) => cart.activities.length >= 8);
  });

  const hasMakercart = computed(
    () => makercarts.value.length > 0 || signatureMakercarts.value.length > 0
  );
  const hasMakercrates = computed(() => makercrates.value.length > 0);
  const availableCarts = computed(() => {
    const carts = makercarts.value || [];
    const cartOptions = carts.map((cart) => ({
      value: cart.id,
      text: cart.name,
    }));

    cartOptions.push({
      value: "create_new",
      text: "Add new Makercart",
    });

    return cartOptions;
  });

  const addToCart = async (item, grade, focus, numberOfStudents = 80) => {
    try {
      await store.dispatch("addToCart", {
        item,
        grade,
        focus,
        numberOfStudents,
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const resetTotal = () => {
    total.value = {
      grandTotal: 0,
      originalPrice: 0,
      percartTotal: 0,
      posterPrice: 0,
      singleSubtotal: 0,
      makercratePrice: 0,
    };
  };

  const removeFromCart = (index) => {
    store.dispatch("removeFromCart", index);
  };

  const clearCart = () => {
    store.dispatch("clearCart");
  };

  const initializeCart = () => {
    store.dispatch("initializeCart");
  };

  const submitCart = async (submitFunction) => {
    try {
      const result = await store.dispatch("submitCart", submitFunction);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateCartItem = (index, updates) => {
    store.dispatch("updateCartItem", { index, updates });
  };

  const setAutoProcessCart = (shouldProcess) => {
    store.dispatch("setAutoProcessCart", shouldProcess);
  };

  const getCartItemById = (id) => {
    return store.getters.getCartItemById(id);
  };

  const isItemInCart = (id) => {
    return !!getCartItemById(id);
  };

  // API Integration Functions
  const syncCartWithAPI = async () => {
    try {
      const response = await userCartService.getUserCart(locale.value);
      if (!response.success || !response.data) {
        if (response.message === "Cart not found") {
          makercarts.value = [];
          singleActivities.value = [];
          signatureMakercarts.value = [];
          makercrates.value = [];
          cartId.value = null;
          resetTotal();
          return { success: true };
        }
        return {
          success: false,
          error: response.message || "Failed to load cart",
        };
      }
      if (response.success && response.data) {
        const { bundles, single, signatures, makercrate } = response.data;
        cartId.value = response.data.id;

        // Update total values
        total.value.grandTotal = response.data.grand_total || 0;
        total.value.originalPrice = response.data.original_price || 0;
        total.value.percartTotal = response.data.percart_total || 0;
        total.value.posterPrice = response.data.poster_price || 0;
        total.value.singleSubtotal = response.data.singe_sub_total || 0;
        total.value.signaturePrice = response.data.signature_price || 0;
        total.value.customCartPrice = response.data.custom_cart_price || 0;

        // Convert API bundles to makercarts format
        makercarts.value = (bundles || []).map((bundle) => ({
          id: bundle.id,
          name: bundle.name || `Custom Makercart ${bundle.id}`,
          activityCount: bundle.items?.length || 0,
          maxActivities: locale.value === "US" ? 4 : 8,
          badge: "No Badge",
          lastAdded: bundle.last_added || "-",
          subtotal: (bundle.final_price || 0) * (Number(bundle.quantity) || 1),
          originalPrice:
            (bundle.sticker_price || 0) * (Number(bundle.quantity) || 1),
          isEditing: false,
          isExpanded: true,
          quantity: bundle.quantity || 1,
          activities: (bundle.items || []).map((item) => ({
            entry_id: item.id.toString(),
            makercart_id: item.airtable_id,
            title: item.title,
            grade: item.grade,
            focus: item.focus,
            projects: item.number_of_projects,
            includingPoster: item.add_poster,
            price: item.price,
            addedAt: item.created_at,
          })),
        }));

        // Convert API single activities
        singleActivities.value = (single?.items || []).map((item) => ({
          entry_id: item.id.toString(),
          makercart_id: item.airtable_id,
          title: item.title,
          grade: item.grade,
          focus: item.focus,
          projects: item.number_of_projects,
          includingPoster: item.add_poster,
          price: item.final_price,
          addedAt: item.created_at,
        }));

        // Convert API signature makercarts
        signatureMakercarts.value = (signatures || []).map((signature) => ({
          id: signature.id,
          name: signature.name || `Signature Makercart ${signature.id}`,
          activityCount: signature.items?.length || 0,
          maxActivities: 4,
          badge: "Signature",
          lastAdded: signature.last_added || "-",
          subtotal: signature.final_price || 0,
          originalPrice: signature.sticker_price || 0,
          isEditing: false,
          isExpanded: true,
          isSignature: true,
          quantity: signature.quantity || 1,
          theme: signature.theme,
          type: signature.type,
          // Store original signature data for matching
          originalSignatureId: signature.original_signature_id || signature.id,
          selectedGrade:
            signature.items?.[0]?.grade === "Upper Elementary"
              ? "upper_elementary"
              : "middle_school",
          activities: (signature.items || []).map((item) => ({
            entry_id: item.id.toString(),
            makercart_id: item.airtable_id,
            title: item.title,
            grade: item.grade,
            focus: item.focus,
            projects: item.number_of_projects,
            includingPoster: item.add_poster,
            price: item.final_price || item.price,
            addedAt: item.created_at,
            isSignature: true,
          })),
        }));

        // Convert API makercrates (pre-built kits added from the catalog)
        makercrates.value = (makercrate || []).map((crate) => ({
          id: crate.id,
          name: crate.name || `Makercrate ${crate.id}`,
          type: crate.type,
          theme: crate.theme,
          lastAdded: crate.last_added || "-",
          subtotal: (crate.final_price || 0) * (Number(crate.quantity) || 1),
          originalPrice: crate.sticker_price || 0,
          quantity: crate.quantity || 1,
          isExpanded: true,
          isEditing: false,
          activityCount: crate.items?.length || 0,
          maxActivities: crate.items?.length || 4,
          studentsPerKit: crate.students_per_kit || "",
          instructionalHours: crate.instructional_hours || "",
          image: "/images/maker-cart/makercrate-logo-v3.png",
          activities: (crate.items || []).map((item) => ({
            entry_id: item.id.toString(),
            makercart_id: item.airtable_id,
            title: item.title,
            grade: item.grade,
            focus: item.focus,
            projects: item.number_of_projects,
            includingPoster: item.add_poster,
            price: item.final_price || item.price,
            addedAt: item.created_at,
          })),
        }));

        total.value.makercratePrice =
          response.data.makercrate_price ??
          makercrates.value.reduce(
            (sum, crate) => sum + (crate.subtotal || 0),
            0
          );

        // Use API pricing instead of local calculations
        // Note: Pricing is now handled by the API response
        return { success: true };
      }
    } catch (error) {
      resetTotal();
      console.error("Error syncing cart with API:", error);
      return { success: false, error: error.message };
    }
  };

  // Helper function to refresh cart data after any action
  const refreshCartAfterAction = async () => {
    try {
      const refreshResult = await syncCartWithAPI();
      if (!refreshResult.success) {
        console.warn(
          "Failed to refresh cart data after action:",
          refreshResult.error
        );
      }
      return refreshResult;
    } catch (error) {
      console.error("Error refreshing cart after action:", error);
      return { success: false, error: error.message };
    }
  };

  const createBundleAPI = async () => {
    try {
      const response = await userCartService.createBundle(locale.value);
      if (response.success && response.data) {
        return response.data.id;
      }
      throw new Error("Failed to create bundle");
    } catch (error) {
      console.error("Error creating bundle via API:", error);
      throw error;
    }
  };

  const addActivityToBundleAPI = async (bundleId, activityData) => {
    try {
      const requestData = {
        grade: activityData.grade,
        focus: activityData.focus,
        number_of_projects: activityData.projects || 80,
        airtable_id: activityData.makercart_id,
        add_poster: activityData.includingPoster || false,
        locale: locale.value,
      };

      const response = await userCartService.insertToBundle(
        bundleId,
        requestData
      );
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error("Failed to add activity to bundle");
    } catch (error) {
      console.error("Error adding activity to bundle via API:", error);
      throw error;
    }
  };

  const addSingleActivityAPI = async (activityData) => {
    try {
      const requestData = {
        grade: activityData.grade,
        focus: activityData.focus,
        number_of_projects: activityData.projects || 80,
        airtable_id: activityData.makercart_id,
        add_poster: activityData.includingPoster || false,
        locale: locale.value,
      };

      const response = await userCartService.addSingleActivity(requestData);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error("Failed to add single activity");
    } catch (error) {
      console.error("Error adding single activity via API:", error);
      throw error;
    }
  };

  const updateActivityAPI = async (itemId, updates) => {
    try {
      const requestData = {
        grade: updates.grade,
        focus: updates.focus,
        number_of_projects: updates.projects || 80,
        airtable_id: updates.makercart_id,
        add_poster: updates.includingPoster || false,
        locale: "US",
      };

      const response = await userCartService.updateActivity(
        itemId,
        requestData
      );
      if (response.success) {
        return response.data;
      }
      throw new Error("Failed to update activity");
    } catch (error) {
      console.error("Error updating activity via API:", error);
      throw error;
    }
  };

  const removeActivityAPI = async (itemId) => {
    try {
      const response = await userCartService.removeFromBundle(itemId);
      if (response.success) {
        return true;
      }
      throw new Error("Failed to remove activity");
    } catch (error) {
      console.error("Error removing activity via API:", error);
      throw error;
    }
  };

  const removeBundleAPI = async (bundleId) => {
    try {
      const response = await userCartService.removeBundle(bundleId);
      if (response.success) {
        return true;
      }
      throw new Error("Failed to remove bundle");
    } catch (error) {
      console.error("Error removing bundle via API:", error);
      throw error;
    }
  };

  const convertSingleToBundleAPI = async () => {
    try {
      const response = await userCartService.convertSingleToBundle(
        cartId.value
      );

      if (response.success) {
        return true;
      }

      throw new Error("Failed to convert single to bundle");
    } catch (error) {
      console.error("Error converting single to bundle via API:", error);
      throw error;
    }
  };

  const initMakercarts = async () => {
    if (makercartsInitialized) return;
    if (makercartsInitPromise) {
      await makercartsInitPromise;
      return;
    }

    makercartsInitPromise = (async () => {
      const apiSyncResult = await syncCartWithAPI();

      if (apiSyncResult.success) {
        if (makercarts.value.length > 0) {
          cartIdCounter =
            Math.max(...makercarts.value.map((cart) => cart.id), 0) + 1;
        }
        makercartsInitialized = true;
        return;
      }

      console.error("API sync failed:", apiSyncResult.error);
      makercarts.value = [];
      singleActivities.value = [];
      quoteRequested.value = false;
      quoteGenerated.value = false;
      cartIdCounter = 1;
      makercartsInitialized = true;
    })();

    try {
      await makercartsInitPromise;
    } finally {
      makercartsInitPromise = null;
    }
  };

  const addNewMakercart = async () => {
    cartLoadingCount.value++;
    try {
      const bundleId = await createBundleAPI(locale.value);

      // Refresh cart data from API to get the latest state
      await refreshCartAfterAction();

      // Find the newly created cart from refreshed data
      const newCart = makercarts.value.find((cart) => cart.id === bundleId);
      if (!newCart) {
        throw new Error("New cart not found after creation");
      }

      return newCart;
    } catch (error) {
      console.error("Failed to create new makercart:", error);
      throw error;
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const deleteMakercart = async (cartId) => {
    cartLoadingCount.value++;
    try {
      await removeBundleAPI(cartId);

      // Refresh cart data from API to get the latest state
      await refreshCartAfterAction();

      return { success: true };
    } catch (error) {
      console.error("Failed to delete makercart:", error);
      throw error;
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const toggleMakercrateView = (makercrateCartId) => {
    const makercrate = makercrates.value.find(
      (crate) => crate.id === makercrateCartId
    );
    if (makercrate) {
      makercrate.isExpanded = !makercrate.isExpanded;
    }
  };

  const convertSingleToBundle = async () => {
    cartLoadingCount.value++;
    try {
      await convertSingleToBundleAPI();

      // Refresh cart data from API to get the latest state
      await refreshCartAfterAction();

      return true;
    } catch (error) {
      console.error("Error converting single to bundle:", error);
      throw error;
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const duplicateMakercart = async (cartId, cartType) => {
    cartLoadingCount.value++;

    try {
      const originalCart =
        cartType === "signature"
          ? signatureMakercarts.value.find((cart) => cart.id === cartId)
          : makercarts.value.find((cart) => cart.id === cartId);
      if (!originalCart) {
        throw new Error("Cart not found");
      }

      // Call API to duplicate the bundle
      const response = await userCartService.duplicateBundle(cartId);
      if (!response.success || !response.data) {
        throw new Error("Failed to duplicate bundle via API");
      }

      // Refresh cart data from API to ensure synchronization
      await syncCartWithAPI();
    } catch (error) {
      console.error("Error duplicating makercart:", error);
      throw error;
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  // Makercrates are bundle rows too, so they share the cart name editing flow
  const findEditableCart = (cartId) =>
    makercarts.value.find((cart) => cart.id === cartId) ||
    signatureMakercarts.value.find((cart) => cart.id === cartId) ||
    makercrates.value.find((cart) => cart.id === cartId);

  const startEditingCartName = (cartId) => {
    const cart = findEditableCart(cartId);
    if (cart) {
      cart.isEditing = true;
    }
  };

  const finishEditingCartName = async (cartId, newName) => {
    const cart = findEditableCart(cartId);
    cartLoadingCount.value++;
    try {
      if (cart) {
        await userCartService.updateBundleName(cartId, newName);

        if (newName.trim()) {
          cart.name = newName.trim();
        }
        cart.isEditing = false;
      }
    } catch (error) {
      console.error("Error finishing editing cart name:", error);
      throw error;
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const toggleCartView = (cartId, cartType) => {
    if (cartType === "signature") {
      const cart = signatureMakercarts.value.find((cart) => cart.id === cartId);
      if (cart) cart.isExpanded = !cart.isExpanded;
    } else {
      const cart = makercarts.value.find((cart) => cart.id === cartId);
      if (cart) cart.isExpanded = !cart.isExpanded;
    }
  };

  const createNewMakercart = async () => {
    try {
      const newCart = await addNewMakercart();
      return newCart.id;
    } catch (error) {
      console.error("Error creating new makercart:", error);
      return null;
    }
  };

  const calculateBundledSubtotal = () => {
    // Use API pricing from cart data
    return makercarts.value.reduce(
      (total, cart) => total + (cart.subtotal || 0),
      0
    );
  };

  const calculateBundledOriginalSubtotal = () => {
    // Use API pricing from cart data
    return makercarts.value.reduce((total, cart) => {
      return total + (cart.subtotal || 0);
    }, 0);
  };

  const calculateSingleActivitySubtotal = () => {
    // Use API pricing from single activities data
    return singleActivities.value.reduce((total, activity) => {
      return total + (activity.price || 0);
    }, 0);
  };

  // Individual pricing for bundled activities only (used for savings comparison)

  const calculateSavings = computed(() => {
    // Savings compares Original bundle pricing vs Final bundle pricing
    const bundledFinalTotal = total.value.percartTotal;
    const bundledOriginalTotal = total.value.originalPrice;
    const signatureFinalTotal = total.value.signaturePrice;
    const customCartFinalTotal = total.value.customCartPrice;

    const signatureSavings = Math.max(
      0,
      customCartFinalTotal - signatureFinalTotal
    );
    const customCartSavings = Math.max(
      0,
      bundledOriginalTotal - bundledFinalTotal
    );
    const cartTotal = bundledOriginalTotal + customCartFinalTotal;
    const cartFinalTotal = bundledFinalTotal + customCartFinalTotal;
    const savings = Math.max(0, signatureSavings + customCartSavings);
    const percentage =
      bundledOriginalTotal > 0 ? Math.round((savings / cartTotal) * 100) : 0;

    return {
      amount: savings,
      percentage,
      base: cartFinalTotal,
      compare: cartTotal,
    };
  });

  const removeActivityFromMakercart = async (cartId, activityId) => {
    cartLoadingCount.value++;
    try {
      const cartIndex = makercarts.value.findIndex(
        (cart) => cart.id === cartId
      );
      if (cartIndex === -1) {
        return { success: false, error: "Cart not found" };
      }

      const activityIndex = makercarts.value[cartIndex].activities.findIndex(
        (activity) =>
          activity.entry_id === activityId ||
          activity.makercart_id === activityId
      );

      if (activityIndex === -1) {
        return { success: false, error: "Activity not found" };
      }

      const activity = makercarts.value[cartIndex].activities[activityIndex];

      await removeActivityAPI(parseInt(activity.entry_id));

      // Refresh cart data from API to get the latest state with updated pricing
      await refreshCartAfterAction();

      // Find the updated cart from refreshed data
      const updatedCart = makercarts.value.find((cart) => cart.id === cartId);
      if (!updatedCart) {
        return {
          success: false,
          error: "Cart not found after removing activity",
        };
      }

      return { success: true, cart: updatedCart };
    } catch (error) {
      console.error("Error removing activity from makercart:", error);
      return { success: false, error: error.message };
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const addActivityToMakercart = async (cartId, activityData) => {
    cartLoadingCount.value++;
    try {
      const cartIndex = makercarts.value.findIndex(
        (cart) => cart.id === cartId
      );

      if (cartIndex === -1) {
        throw new Error("Selected cart not found");
      }
      const activities = makercarts.value[cartIndex].activities;

      const maxActivities = locale.value === "US" ? 4 : 8;

      if (!makercarts.value[cartIndex].activities) {
        makercarts.value[cartIndex].activities = [];
      }

      if (activities.length >= maxActivities) {
        throw new Error("The cart is full");
      }

      await addActivityToBundleAPI(cartId, activityData);

      // Refresh cart data from API to get the latest state with updated pricing
      await refreshCartAfterAction();

      // Find the updated cart from refreshed data
      const updatedCart = makercarts.value.find((cart) => cart.id === cartId);
      if (!updatedCart) {
        throw new Error("Cart not found after adding activity");
      }

      return { success: true, cart: updatedCart };
    } catch (error) {
      console.error("Error adding activity to makercart:", error);
      return { success: false, error: error.message };
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const isActivityInSingles = (activityId) => {
    return singleActivities.value.some(
      (activity) => activity.makercart_id === activityId
    );
  };

  const moveActivityBetweenCarts = async (
    sourceCartId,
    targetCartId,
    activityData
  ) => {
    cartLoadingCount.value++;
    try {
      const identifier = activityData.entry_id || activityData.makercart_id;

      // Find the activity to move
      const sourceCart = makercarts.value.find(
        (cart) => cart.id === sourceCartId
      );
      if (!sourceCart) {
        throw new Error("Source cart not found");
      }

      const targetCart = makercarts.value.find(
        (cart) => cart.id === targetCartId
      );

      if (targetCart && targetCart.activities.length === 4) {
        return { success: false, error: "The cart is full" };
      }

      const activity = sourceCart.activities.find(
        (act) => act.entry_id === identifier || act.makercart_id === identifier
      );
      if (!activity) {
        throw new Error("Activity not found in source cart");
      }

      let finalTargetCartId = targetCartId;
      if (targetCartId === "create_new") {
        // Create new cart first
        finalTargetCartId = null;
      }

      // Use the new API to convert single item to bundle (move to target cart)
      await userCartService.moveItemBetweenBundle(
        parseInt(activity.entry_id),
        finalTargetCartId
      );

      // Refresh cart data from API to get the latest state
      await refreshCartAfterAction();

      return {
        success: true,
      };
    } catch (error) {
      console.error("Error moving activity between carts:", error);
      return { success: false, error: error.message };
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const duplicateActivityInCart = async (cartId, activityData, cartType) => {
    cartLoadingCount.value++;
    try {
      const carts =
        cartType === "custom" ? makercarts.value : signatureMakercarts.value;
      const cartIndex = carts.findIndex((cart) => cart.id === cartId);
      if (cartIndex === -1) {
        throw new Error("Cart not found");
      }

      const activities = carts[cartIndex].activities;

      const maxActivities = locale.value === "US" ? 4 : 8;

      // Check if current cart is full
      if (activities.length >= maxActivities) {
        // Create a new cart for the duplicated activity
        const newCart = await addNewMakercart();
        newCart.name = `${carts[cartIndex].name} (Copy)`;

        // Add the duplicated activity to the new cart via API
        const addResult = await addActivityToMakercart(
          newCart.id,
          activityData
        );
        if (!addResult.success) {
          throw new Error("Failed to add duplicated activity to new cart");
        }

        return {
          success: true,
          cart: addResult.cart,
          isNewCart: true,
          message:
            "Your current cart is full. This activity will be duplicated into a new cart.",
        };
      } else {
        // Add to current cart via API
        const addResult = await addActivityToMakercart(
          cartId,
          activityData,
          cartType
        );
        if (!addResult.success) {
          throw new Error("Failed to add duplicated activity to current cart");
        }

        return {
          success: true,
          cart: addResult.cart,
          isNewCart: false,
          message: "Activity duplicated successfully",
        };
      }
    } catch (error) {
      console.error("Error duplicating activity:", error);
      return { success: false, error: error.message };
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const deleteActivityFromCart = async (cartId, activityId) => {
    cartLoadingCount.value++;
    try {
      const cartIndex = makercarts.value.findIndex(
        (cart) => cart.id === cartId
      );
      if (cartIndex === -1) {
        throw new Error("Cart not found");
      }

      const activityIndex = makercarts.value[cartIndex].activities.findIndex(
        (activity) =>
          activity.entry_id === activityId ||
          activity.makercart_id === activityId
      );
      if (activityIndex === -1) {
        throw new Error("Activity not found");
      }

      const activity = makercarts.value[cartIndex].activities[activityIndex];

      await removeActivityAPI(parseInt(activity.entry_id));

      // Refresh cart data from API to get the latest state with updated pricing
      await refreshCartAfterAction();

      // Find the updated cart from refreshed data
      const updatedCart = makercarts.value.find((cart) => cart.id === cartId);
      if (!updatedCart) {
        throw new Error("Cart not found after deleting activity");
      }

      return { success: true, cart: updatedCart };
    } catch (error) {
      console.error("Error deleting activity:", error);
      return { success: false, error: error.message };
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const updateActivityInMakercart = async (
    cartId,
    activityId,
    updates,
    cartType
  ) => {
    cartLoadingCount.value++;
    try {
      const carts =
        cartType === "custom" ? makercarts.value : signatureMakercarts.value;
      const cartIndex = carts.findIndex((cart) => cart.id === cartId);
      if (cartIndex === -1) {
        throw new Error("Cart not found");
      }

      const activityIndex = carts[cartIndex].activities.findIndex(
        (activity) =>
          activity.entry_id === activityId ||
          activity.makercart_id === activityId
      );
      if (activityIndex === -1) {
        throw new Error("Activity not found");
      }

      const activity = carts[cartIndex].activities[activityIndex];

      await updateActivityAPI(parseInt(activity.entry_id), updates);

      // Refresh cart data from API to get the latest state with updated pricing
      await refreshCartAfterAction();

      return {
        success: true,
      };
    } catch (error) {
      console.error("Error updating activity:", error);
      return { success: false, error: error.message };
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const convertActivityToSingle = async (cartId, activityData, cartType) => {
    cartLoadingCount.value++;
    try {
      const carts =
        cartType === "custom" ? makercarts.value : signatureMakercarts.value;
      const cartIndex = carts.findIndex((cart) => cart.id === cartId);
      if (cartIndex === -1) {
        throw new Error("Cart not found");
      }

      const activityIndex = carts[cartIndex].activities.findIndex(
        (activity) =>
          activity.entry_id === activityData.entry_id ||
          activity.makercart_id === activityData.makercart_id
      );
      if (activityIndex === -1) {
        throw new Error("Activity not found");
      }

      const activity = carts[cartIndex].activities[activityIndex];

      // Use the new API to convert bundle item to single
      await userCartService.convertBundleItemToSingle(
        parseInt(activity.entry_id)
      );

      // Refresh cart data from API to get the latest state with updated pricing
      await refreshCartAfterAction();

      // Find the updated cart and single activity from refreshed data
      const updatedCart = carts.find((cart) => cart.id === cartId);
      if (!updatedCart) {
        throw new Error("Cart not found after converting activity to single");
      }

      const singleActivity = singleActivities.value.find(
        (act) => act.makercart_id === activityData.makercart_id
      );

      return {
        success: true,
        cart: updatedCart,
        singleActivity: singleActivity,
      };
    } catch (error) {
      console.error("Error converting activity to single:", error);
      return { success: false, error: error.message };
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const addSingleActivity = async (activityData) => {
    cartLoadingCount.value++;
    try {
      if (isActivityInSingles(activityData.makercart_id)) {
        throw new Error("The activity is already added");
      }

      await addSingleActivityAPI(activityData);

      // Refresh cart data from API to get the latest state with updated pricing
      await refreshCartAfterAction();

      return { success: true };
    } catch (error) {
      console.error("Error adding single activity:", error);
      return { success: false, error: error.message };
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const removeSingleActivity = async (activityId) => {
    cartLoadingCount.value++;
    try {
      const index = singleActivities.value.findIndex(
        (activity) =>
          activity.entry_id === activityId ||
          activity.makercart_id === activityId
      );

      if (index === -1) {
        return { success: false, error: "Activity not found" };
      }

      const activity = singleActivities.value[index];

      await removeActivityAPI(parseInt(activity.entry_id));

      // Refresh cart data from API to get the latest state with updated pricing
      await refreshCartAfterAction();

      return { success: true };
    } catch (error) {
      console.error("Error removing single activity:", error);
      return { success: false, error: error.message };
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const mergeCustomMakercart = async (matchingCart, sourceCart = null) => {
    const originalQuantity = Number(matchingCart.quantity) || 1;
    const addedQuantity = sourceCart ? Number(sourceCart.quantity) || 1 : 1;
    const response = await userCartService.updateBundleQuantity(
      matchingCart.id,
      originalQuantity + addedQuantity
    );
    if (!response.success) {
      throw new Error(response.message || "Failed to update cart quantity");
    }

    if (sourceCart) {
      try {
        await removeBundleAPI(sourceCart.id);
      } catch (error) {
        // Keep both original carts if removing the redundant bundle fails.
        try {
          const rollback = await userCartService.updateBundleQuantity(
            matchingCart.id,
            originalQuantity
          );
          if (!rollback.success) {
            throw new Error(rollback.message || "Failed to restore quantity");
          }
        } catch (rollbackError) {
          console.error("Failed to restore cart quantity:", rollbackError);
          throw new Error(
            "Failed to merge carts and restore quantity. Please reload your cart."
          );
        }
        throw error;
      }
    }

    const refreshResult = await refreshCartAfterAction();
    if (!refreshResult.success) {
      throw new Error("Cart quantity updated, but failed to refresh cart");
    }
    const cart = makercarts.value.find((cart) => cart.id === matchingCart.id);
    if (!cart) throw new Error("Merged cart not found after updating quantity");
    return { success: true, cart };
  };

  const handleMakercartSelection = async (cartValue, activityData) => {
    cartLoadingCount.value++;
    try {
      const syncResult = await syncCartWithAPI();
      if (!syncResult.success) {
        throw new Error(syncResult.error || "Failed to load cart");
      }

      const sourceCart =
        cartValue === "create_new"
          ? null
          : makercarts.value.find((cart) => cart.id === cartValue);
      if (cartValue !== "create_new" && !sourceCart) {
        throw new Error("Selected cart not found");
      }
      const maxActivities = locale.value === "US" ? 4 : 8;
      if (sourceCart && sourceCart.activities.length >= maxActivities) {
        throw new Error("The cart is full");
      }

      const configuration = getCustomCartConfiguration([
        ...(sourceCart?.activities || []),
        {
          ...activityData,
          projects: activityData.projects || 80,
          includingPoster: activityData.includingPoster || false,
        },
      ]);
      const matchingCart = configuration
        ? makercarts.value.find(
            (cart) =>
              cart.id !== sourceCart?.id &&
              getCustomCartConfiguration(cart.activities) === configuration
          )
        : null;
      if (matchingCart) {
        return await mergeCustomMakercart(matchingCart, sourceCart);
      }

      let targetCartId = cartValue;

      if (cartValue === "create_new") {
        targetCartId = await createNewMakercart();
        if (!targetCartId) {
          return { success: false, error: "Failed to create new cart" };
        }
      }

      const result = await addActivityToMakercart(targetCartId, activityData);
      return result;
    } catch (error) {
      console.error("Error handling makercart selection:", error);
      return { success: false, error: error.message };
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const requestQuote = async (email, formData) => {
    try {
      quoteRequested.value = true;

      // Prepare request body
      const requestBody = {
        email,
        organization_name: formData.organization_name,
        attention_to: formData.attention_to,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        country: formData.country,
        locale: locale.value,
      };

      await userCartService.requestAQuote(requestBody);

      quoteGenerated.value = true;

      return { success: true };
    } catch (error) {
      console.error("Error requesting quote:", error);
      quoteRequested.value = false;
      return { success: false, error: error.message };
    }
  };

  const resetCart = () => {
    if (!quoteRequested.value) return;

    makercarts.value = [];
    singleActivities.value = [];
    signatureMakercarts.value = [];
    makercrates.value = [];
    quoteRequested.value = false;
    quoteGenerated.value = false;
    cartIdCounter = 1;
    makercartsInitialized = false;
  };

  // Signature Makercart Functions
  const addSignatureMakercart = async (
    signatureData,
    selectedGrade,
    numberOfStudents = 80,
    includingPoster = false
  ) => {
    cartLoadingCount.value++;
    try {
      // Prepare data for API call
      const requestData = {
        grade:
          selectedGrade === "upper_elementary"
            ? "Upper Elementary"
            : "Middle School",
        focus: null, // Signature makercarts don't have specific focus
        number_of_projects: numberOfStudents,
        add_poster: includingPoster,
        locale: "US",
      };

      // Call API to add signature makercart
      const response = await signatureMakercartService.addSignatureMakercart(
        signatureData.id,
        requestData
      );

      if (!response.success) {
        throw new Error(response.error || "Failed to add signature makercart");
      }

      // Refresh cart data from API to get the latest state with updated pricing
      await refreshCartAfterAction();

      return { success: true };
    } catch (error) {
      console.error("Error adding signature makercart:", error);
      return { success: false, error: error.message };
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const removeSignatureMakercart = async (signatureCartId) => {
    cartLoadingCount.value++;
    try {
      const signatureCart = signatureMakercarts.value.find(
        (cart) => cart.id === signatureCartId
      );
      if (!signatureCart) {
        return { success: false, error: "Signature makercart not found" };
      }

      // Call API to remove signature makercart (using removeBundle API)
      await userCartService.removeBundle(signatureCartId);

      // Refresh cart data from API to get the latest state
      await refreshCartAfterAction();

      return { success: true };
    } catch (error) {
      console.error("Error removing signature makercart:", error);
      return { success: false, error: error.message };
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  const getSignatureMakercartById = (id) => {
    return signatureMakercarts.value.find((cart) => cart.id === id);
  };

  const isSignatureMakercartInCart = (signatureData, selectedGrade) => {
    return signatureMakercarts.value.some((cart) => {
      // Check if this signature makercart matches the signature data and grade
      const gradeMatch = cart.selectedGrade === selectedGrade;

      // Match by original signature ID if available, otherwise by theme
      const signatureMatch =
        cart.originalSignatureId === signatureData.id ||
        cart.theme === signatureData.theme;

      return signatureMatch && gradeMatch;
    });
  };

  const convertSignatureToCustom = async (signatureCartId) => {
    try {
      cartLoadingCount.value++;

      const response =
        await signatureMakercartService.convertToCustom(signatureCartId);

      if (response.success) {
        // Refresh cart data after conversion
        await refreshCartAfterAction();
        return { success: true, data: response.data };
      } else {
        return {
          success: false,
          error: response.error || "Failed to convert signature makercart",
        };
      }
    } catch (error) {
      console.error("Error converting signature makercart to custom:", error);
      return {
        success: false,
        error: error.message || "Failed to convert signature makercart",
      };
    } finally {
      cartLoadingCount.value = Math.max(0, cartLoadingCount.value - 1);
    }
  };

  onMounted(() => {
    initMakercarts();
  });

  watch(locale, async (newLocale, oldLocale) => {
    if (newLocale !== oldLocale) {
      await syncCartWithAPI();
    }
  });

  return {
    // State
    cartId,
    cartItems,
    totalActivities,
    hasFullActivityCart,
    isCartEmpty,
    isCartSubmitting,
    canSubmitCart,
    cartValidationMessage,
    cartLastUpdated,
    shouldAutoProcessCart,

    // Actions
    addToCart,
    removeFromCart,
    clearCart,
    initializeCart,
    submitCart,
    updateCartItem,
    setAutoProcessCart,

    // Helpers
    getCartItemById,
    isItemInCart,
    getTotalActivities,

    // Makercart-specific state
    makercarts,
    singleActivities,
    signatureMakercarts,
    makercrates,
    hasMakercart,
    hasMakercrates,
    availableCarts,
    quoteRequested,
    quoteGenerated,

    // Makercart-specific actions
    addNewMakercart,
    deleteMakercart,
    toggleMakercrateView,
    duplicateMakercart,
    startEditingCartName,
    finishEditingCartName,
    findEditableCart,
    toggleCartView,
    createNewMakercart,
    addActivityToMakercart,
    removeActivityFromMakercart,
    handleMakercartSelection,
    addSingleActivity,
    removeSingleActivity,
    isActivityInSingles,
    moveActivityBetweenCarts,
    duplicateActivityInCart,
    deleteActivityFromCart,
    convertActivityToSingle,
    updateActivityInMakercart,
    convertSingleToBundle,
    isCartLoading,
    total,

    // Signature Makercart actions
    addSignatureMakercart,
    removeSignatureMakercart,
    getSignatureMakercartById,
    isSignatureMakercartInCart,
    convertSignatureToCustom,

    // New pricing calculations
    calculateBundledSubtotal,
    calculateBundledOriginalSubtotal,
    calculateSingleActivitySubtotal,
    calculateSavings,

    // Quote functionality
    requestQuote,
    resetCart,

    // API Integration
    syncCartWithAPI,
  };
}
